/**
 * PasteFormulaMenu — Radix popover with history entries + system-clipboard paste.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ClipboardPaste, History, Layers, Sigma, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { cn } from '@/lib/utils';

import { useFormulaClipboard } from '../useFormulaClipboard';
import type {
  FormulaClipboardHistoryEntry,
  FormulaClipboardPayload,
} from '../formula-clipboard.types';

export interface PasteFormulaMenuProps {
  onApply: (payload: FormulaClipboardPayload) => void;
  /** Set true when the formula has at least one group — gate replace confirms. */
  hasExistingGroups: boolean;
}

const PasteFormulaMenu: FC<PasteFormulaMenuProps> = ({ onApply, hasExistingGroups }) => {
  const { history, pasteFromSystem, clearHistory, refreshHistory } = useFormulaClipboard();
  const [open, setOpen] = useState(false);

  const handleSystemPaste = async () => {
    const payload = await pasteFromSystem();
    if (!payload) return;
    if (!confirmReplace(payload, hasExistingGroups)) return;
    onApply(payload);
    setOpen(false);
  };

  const handleHistoryClick = (entry: FormulaClipboardHistoryEntry) => {
    if (!confirmReplace(entry, hasExistingGroups)) return;
    onApply(entry);
    setOpen(false);
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) refreshHistory();
      }}
    >
      <Popover.Trigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 px-2.5 text-xs">
          <ClipboardPaste className="mr-1 h-3.5 w-3.5" aria-hidden />
          Paste
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-80 rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in slide-in-from-top-1"
        >
          <button
            type="button"
            onClick={handleSystemPaste}
            className={cn(
              'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs',
              'hover:bg-accent hover:text-accent-foreground',
            )}
          >
            <ClipboardPaste className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>Paste from clipboard…</span>
          </button>
          <HistoryList history={history} onPick={handleHistoryClick} onClear={clearHistory} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

interface HistoryListProps {
  history: FormulaClipboardHistoryEntry[];
  onPick: (entry: FormulaClipboardHistoryEntry) => void;
  onClear: () => void;
}

const HistoryList: FC<HistoryListProps> = ({ history, onPick, onClear }) => {
  if (history.length === 0) {
    return (
      <p className="px-2 py-2 text-[11px] italic text-muted-foreground">
        Copy a group or formula to build a paste history here.
      </p>
    );
  }
  return (
    <>
      <div className="mt-1 flex items-center justify-between px-2 pb-1 pt-2">
        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <History className="h-3 w-3" aria-hidden />
          Recent
        </span>
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-destructive"
          aria-label="Clear paste history"
        >
          <Trash2 className="h-3 w-3" aria-hidden />
          Clear
        </button>
      </div>
      <ul className="max-h-64 overflow-y-auto">
        {history.map((entry) => (
          <li key={entry.id}>
            <HistoryButton entry={entry} onPick={onPick} />
          </li>
        ))}
      </ul>
    </>
  );
};

interface HistoryButtonProps {
  entry: FormulaClipboardHistoryEntry;
  onPick: (entry: FormulaClipboardHistoryEntry) => void;
}

function formatCopiedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const HistoryButton: FC<HistoryButtonProps> = ({ entry, onPick }) => {
  const Icon = entry.type === 'group' ? Layers : Sigma;
  return (
    <button
      type="button"
      onClick={() => onPick(entry)}
      className={cn(
        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs',
        'hover:bg-accent hover:text-accent-foreground',
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{entry.label}</span>
        <span className="truncate text-[10px] text-muted-foreground">
          {entry.type === 'group' ? 'Group' : 'Whole formula'} · {formatCopiedAt(entry.copiedAt)}
        </span>
      </span>
    </button>
  );
};

function confirmReplace(payload: FormulaClipboardPayload, hasExistingGroups: boolean): boolean {
  if (payload.type !== 'formula' || !hasExistingGroups) return true;
  if (typeof window === 'undefined') return true;
  return window.confirm(
    'Pasting a whole formula will REPLACE all current groups. Continue?',
  );
}

export default PasteFormulaMenu;
